import { useEffect, useRef, useCallback } from 'preact/hooks'
import { signalRService } from '@/services/signalRService'
import type { LandingPageDesignQuestionsEvent } from '@/types/landingPage'

type EventHandler = (...args: unknown[]) => void

export type AgentStreamEvent = {
    Type: 'thinking' | 'content' | 'tool_use' | 'tool_result' | 'result' | 'error' | 'done' | 'thankYouGenerating' | 'thankYouResult' | 'generationFailed' | 'thankYouGenerationFailed' | 'analyzing' | 'rebuilding' | 'rebuilt' | 'thankYouRebuilding' | 'thankYouRebuilt' | 'rebuildError' | 'conversationalResponse' | 'assistantMessageAppended' | 'chatStateChanged' | 'userMessageAppended' | 'token_usage'
    Content?: string
    Tool?: string
    InputTokens?: number
    OutputTokens?: number
    /**
     * Model id that produced the content of this event. Set on
     * conversationalResponse (analyzer's model) and assistantMessageAppended
     * (agent's model for the rebuild closer). Drives MessageTokenFooter on
     * the just-finalized chat bubble without requiring a session reload.
     */
    ModelId?: string | null
    /**
     * Sub-classifier for token_usage events. Today the only value is 'partial' —
     * cumulative output_tokens snapped from Anthropic's message_delta. Consumed
     * server-side by AgentCostRecorder.
     */
    Phase?: 'partial'
    /** Set on conversationalResponse when the orchestrator will follow up with a rebuild. */
    WillRebuild?: boolean
    /**
     * Set on chatStateChanged (when state is 'Active') and userMessageAppended.
     * Lets spectator dashboards attribute the locked input and user message bubble
     * without an extra user lookup.
     */
    TurnUserId?: string | null
    TurnUserName?: string | null
    /**
     * On a conversationalResponse that will be followed by a rebuild, tells the
     * dashboard whether to show the targeted-edit "Applying changes…" indicator
     * ('edit') or the full-rebuild indicator ('rebuild').
     */
    RebuildMode?: 'edit' | 'rebuild'
}

interface UseLandingPageSignalROptions {
    webId: string | undefined
    onThinking: (content: string) => void
    onContent: (content: string) => void
    onToolUse: (tool: string) => void
    onToolResult: (tool: string) => void
    onResult: (event: AgentStreamEvent) => void
    onError: (message: string) => void
    onDone: () => void
    onThankYouGenerating?: () => void
    onThankYouResult?: (event: AgentStreamEvent) => void
    onGenerationFailed?: (message: string) => void
    onThankYouGenerationFailed?: (message: string) => void
    onAnalyzing?: () => void
    onRebuilding?: () => void
    onRebuilt?: () => void
    onThankYouRebuilding?: () => void
    onThankYouRebuilt?: () => void
    onRebuildError?: (message: string) => void
    onConversationalResponse?: (content: string, willRebuild: boolean, modelId: string | null, outputTokens: number | null, rebuildMode?: 'edit' | 'rebuild') => void
    /**
     * Fired when the server appends a new visible assistant bubble outside the
     * sendMessage round-trip — currently the rebuild "closer" written by
     * AgentCostRecorder ("Landing page updated." with the agent's Opus model).
     * Lets the dashboard append it to chat with model+tokens stamped so the
     * MessageTokenFooter renders immediately.
     */
    onAssistantMessageAppended?: (content: string, modelId: string | null, outputTokens: number | null) => void
    /**
     * Fired when the chat session's turn lock changes — Active means another
     * user is mid-turn (or this user just acquired it); Idle means the turn
     * ended and any spectator's input should re-enable. Carries the holder's
     * display name on Active so the input placeholder can attribute it.
     */
    onChatStateChanged?: (state: 'Idle' | 'Active', turnUserId: string | null, turnUserName: string | null) => void
    /**
     * Fired when another user appended a chat message — lets spectator
     * dashboards render the new user bubble in real time instead of waiting
     * for the next session refresh. Dedupe by createdById + content when this
     * client is the sender (HTTP response already added the message locally).
     */
    onUserMessageAppended?: (content: string, createdById: string | null, createdByName: string | null) => void
    /**
     * Webster paused generation to ask design questions. Arrives on its own SignalR
     * method rather than the generic generation event, because it carries a structured
     * form to render, not a status string.
     */
    onDesignQuestions?: (event: LandingPageDesignQuestionsEvent) => void
}

/**
 * Manages SignalR subscription for landing page generation streaming events.
 * Joins the `landingpage-{webId}` group and listens for `LandingPageGenerationEvent`.
 *
 * Callbacks are stored in refs so the effect only re-runs when webId changes,
 * preventing mid-generation unsubscribe/resubscribe from callback identity changes.
 */
export function useLandingPageSignalR({
    webId,
    onThinking,
    onContent,
    onToolUse,
    onToolResult,
    onResult,
    onError,
    onDone,
    onThankYouGenerating,
    onThankYouResult,
    onGenerationFailed,
    onThankYouGenerationFailed,
    onAnalyzing,
    onRebuilding,
    onRebuilt,
    onThankYouRebuilding,
    onThankYouRebuilt,
    onRebuildError,
    onConversationalResponse,
    onAssistantMessageAppended,
    onChatStateChanged,
    onUserMessageAppended,
    onDesignQuestions,
}: UseLandingPageSignalROptions) {
    const handlerRef = useRef<{ handler: EventHandler; group: string } | null>(null)
    const questionsHandlerRef = useRef<EventHandler | null>(null)

    // Store callbacks in ref so the effect doesn't depend on them
    const callbacksRef = useRef<typeof callbacks>(null!)
    const callbacks = {
        onThinking, onContent, onToolUse, onToolResult, onResult, onError, onDone, onThankYouGenerating, onThankYouResult, onGenerationFailed, onThankYouGenerationFailed, onAnalyzing, onRebuilding, onRebuilt, onThankYouRebuilding, onThankYouRebuilt, onRebuildError, onConversationalResponse, onAssistantMessageAppended, onChatStateChanged, onUserMessageAppended, onDesignQuestions,
    }
    callbacksRef.current = callbacks

    const cleanup = useCallback(() => {
        const entry = handlerRef.current
        if (entry) {
            signalRService.off('LandingPageGenerationEvent', entry.handler)
            if (questionsHandlerRef.current) {
                signalRService.off('LandingPageDesignQuestions', questionsHandlerRef.current)
                questionsHandlerRef.current = null
            }
            signalRService.leaveGroup(entry.group)
            handlerRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!webId) return

        const group = `landingpage-${webId}`
        let cancelled = false

        const handleEvent: EventHandler = (payload: unknown) => {
            const raw = payload as Record<string, unknown>
            // Handle both PascalCase (C# default) and camelCase (JS SignalR default)
            const event: AgentStreamEvent = {
                Type: (raw.Type ?? raw.type) as AgentStreamEvent['Type'],
                Content: (raw.Content ?? raw.content) as string | undefined,
                Tool: (raw.Tool ?? raw.tool) as string | undefined,
                InputTokens: (raw.InputTokens ?? raw.inputTokens) as number | undefined,
                OutputTokens: (raw.OutputTokens ?? raw.outputTokens) as number | undefined,
                ModelId: (raw.ModelId ?? raw.modelId) as string | null | undefined,
                Phase: (raw.Phase ?? raw.phase) as 'partial' | undefined,
                WillRebuild: (raw.WillRebuild ?? raw.willRebuild) as boolean | undefined,
                TurnUserId: (raw.TurnUserId ?? raw.turnUserId) as string | null | undefined,
                TurnUserName: (raw.TurnUserName ?? raw.turnUserName) as string | null | undefined,
                RebuildMode: (raw.RebuildMode ?? raw.rebuildMode) as 'edit' | 'rebuild' | undefined,
            }
            const callbacks = callbacksRef.current
            switch (event.Type) {
                case 'thinking':
                    callbacks.onThinking(event.Content ?? '')
                    break
                case 'content':
                    callbacks.onContent(event.Content ?? '')
                    break
                case 'tool_use':
                    callbacks.onToolUse(event.Tool ?? '')
                    break
                case 'tool_result':
                    callbacks.onToolResult(event.Tool ?? '')
                    break
                case 'result':
                    callbacks.onResult(event)
                    break
                case 'error':
                    callbacks.onError(event.Content ?? 'An error occurred during generation')
                    break
                case 'done':
                    callbacks.onDone()
                    break
                case 'thankYouGenerating':
                    callbacks.onThankYouGenerating?.()
                    break
                case 'thankYouResult':
                    callbacks.onThankYouResult?.(event)
                    break
                case 'generationFailed':
                    callbacks.onGenerationFailed?.(event.Content ?? 'Generation failed')
                    break
                case 'thankYouGenerationFailed':
                    callbacks.onThankYouGenerationFailed?.(event.Content ?? 'Thank you page generation failed')
                    break
                case 'analyzing':
                    callbacks.onAnalyzing?.()
                    break
                case 'rebuilding':
                    callbacks.onRebuilding?.()
                    break
                case 'rebuilt':
                    callbacks.onRebuilt?.()
                    break
                case 'thankYouRebuilding':
                    callbacks.onThankYouRebuilding?.()
                    break
                case 'thankYouRebuilt':
                    callbacks.onThankYouRebuilt?.()
                    break
                case 'rebuildError':
                    callbacks.onRebuildError?.(event.Content ?? 'Rebuild failed')
                    break
                case 'conversationalResponse':
                    callbacks.onConversationalResponse?.(
                        event.Content ?? '',
                        event.WillRebuild === true,
                        event.ModelId ?? null,
                        event.OutputTokens ?? null,
                        event.RebuildMode,
                    )
                    break
                case 'assistantMessageAppended':
                    callbacks.onAssistantMessageAppended?.(
                        event.Content ?? '',
                        event.ModelId ?? null,
                        event.OutputTokens ?? null,
                    )
                    break
                case 'token_usage':
                    // Sparse end-of-turn snap. The dashboard no longer drives a live
                    // counter from this; AgentCostRecorder persists the value to the
                    // chat message server-side, MessageTokenFooter reads it back from
                    // the message on the next session load / SignalR userMessageAppended.
                    break
                case 'chatStateChanged':
                    callbacks.onChatStateChanged?.(
                        (event.Content as 'Idle' | 'Active') ?? 'Idle',
                        event.TurnUserId ?? null,
                        event.TurnUserName ?? null,
                    )
                    break
                case 'userMessageAppended':
                    callbacks.onUserMessageAppended?.(
                        event.Content ?? '',
                        event.TurnUserId ?? null,
                        event.TurnUserName ?? null,
                    )
                    break
            }
        }

        const handleDesignQuestions: EventHandler = (payload: unknown) => {
            const raw = payload as Record<string, unknown>
            // Same PascalCase/camelCase tolerance as the generation event — which casing
            // arrives depends on the serializer, not on us.
            // Guarded rather than assumed: this runs inside the SignalR handler, so a
            // malformed payload throwing here would take down the subscription for the
            // rest of the session, not just drop one event.
            const maybeQuestions = raw.Questions ?? raw.questions
            const rawQuestions = (Array.isArray(maybeQuestions) ? maybeQuestions : []) as Record<string, unknown>[]
            callbacksRef.current.onDesignQuestions?.({
                WebId: (raw.WebId ?? raw.webId ?? '') as string,
                Round: (raw.Round ?? raw.round ?? 1) as number,
                MaxRounds: (raw.MaxRounds ?? raw.maxRounds ?? 2) as number,
                Questions: rawQuestions.map((q) => ({
                    Question: (q.Question ?? q.question ?? '') as string,
                    Options: (q.Options ?? q.options ?? []) as string[],
                    Recommended: (q.Recommended ?? q.recommended ?? null) as string | null,
                })),
            })
        }

        signalRService.joinGroup(group).then(() => {
            if (cancelled) {
                signalRService.leaveGroup(group)
                return
            }
            signalRService.on('LandingPageGenerationEvent', handleEvent)
            signalRService.on('LandingPageDesignQuestions', handleDesignQuestions)
            questionsHandlerRef.current = handleDesignQuestions
            handlerRef.current = { handler: handleEvent, group }
        }).catch((err) => {
            console.error('Failed to join landing page SignalR group:', err)
        })

        return () => {
            cancelled = true
            cleanup()
        }
    }, [webId, cleanup])  // Only re-run when webId changes, NOT on callback changes

    return { cleanup }
}
